/**
 * Real-time WebSocket Client Service
 * Manages Socket.IO connection with automatic reconnection and authentication
 */

import { io } from 'socket.io-client';
import { getAccessToken } from './apiClient';
import Logger from '../utils/logger';
import { connectionHealthService } from './connectionHealthService';
import appInsightsService from './appInsightsService';

const EVENTS = {
  // Bus events
  BUS_STATUS_UPDATE: 'bus:status:update',
  BUS_LOCATION_UPDATE: 'bus:location:update',
  BUS_CREATED: 'bus:created',
  BUS_UPDATED: 'bus:updated',
  BUS_DELETED: 'bus:deleted',

  // Schedule events
  SCHEDULE_CREATED: 'schedule:created',
  SCHEDULE_UPDATED: 'schedule:updated',
  SCHEDULE_DELETED: 'schedule:deleted',

  // Maintenance events
  MAINTENANCE_CREATED: 'maintenance:created',
  MAINTENANCE_UPDATED: 'maintenance:updated',
  MAINTENANCE_DELETED: 'maintenance:deleted',
  MAINTENANCE_ALERT: 'maintenance:alert',

  // Dashboard events
  DASHBOARD_REFRESH: 'dashboard:refresh',
  METRICS_UPDATE: 'metrics:update',

  // Real-time location events (from Change Feed)
  LOCATION_UPDATE: 'location:update',
  VEHICLE_LOCATION: 'vehicle:location',

  // Geofence events (from Change Feed)
  GEOFENCE_ENTRY: 'geofence:entry',
  GEOFENCE_EXIT: 'geofence:exit',
  GEOFENCE_ALERT: 'fleet:geofence-alert',

  // Safety events (Phase 4 - Issue #1175)
  SAFETY_EVENT_CREATED: 'safety:event:created',
  SAFETY_EVENT_UPDATED: 'safety:event:updated',
  SAFETY_ALERT_SENT: 'safety:alert:sent',

  // Connection events
  CONNECT: 'connect',
  DISCONNECT: 'disconnect',
  CONNECT_ERROR: 'connect_error',
  RECONNECT: 'reconnect',
  RECONNECT_ATTEMPT: 'reconnect_attempt',
  RECONNECT_ERROR: 'reconnect_error',
  RECONNECT_FAILED: 'reconnect_failed',

  // Authentication events
  AUTH_EXPIRED: 'auth:expired',
  AUTH_EXPIRING: 'auth:expiring'
};

class RealtimeClient {
  constructor() {
    this.socket = null;
    this.isConnected = false;
    this.eventHandlers = new Map();
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.reconnectDelays = [0, 2000, 5000, 10000, 30000]; // Exponential backoff
    this.connectionStatusSubscribers = new Set();
    this.initializationSubscribers = new Set();
    this.isInitialized = false;
    this.connectedAt = null; // Track connection start time
    this.messageCount = 0; // Track messages received
    this.lastMessageTime = null; // Track last message timestamp
  }

  /**
   * Initialize WebSocket connection
   * @param {Object} options - Configuration options
   * @returns {Promise<void>}
   */
  async initialize(options = {}) {
    if (this.isInitialized) {
      Logger.warn('RealtimeClient already initialized');
      return;
    }

    try {
      // Get WebSocket base URL from environment
      // VITE_WS_BASE_URL bypasses Cloudflare for direct Container App WebSocket connection
      // Falls back to VITE_API_BASE_URL or localhost for development
      const wsBaseUrl = import.meta.env.VITE_WS_BASE_URL;
      const apiBaseUrl =
        import.meta.env.VITE_API_BASE_URL ||
        (() => {
          if (import.meta.env.DEV) Logger.warn('[realtimeClient] VITE_API_BASE_URL not set, using localhost fallback');
          return 'http://localhost:3001/api';
        })();
      const wsUrl = wsBaseUrl || apiBaseUrl.replace('/api', '');

      // Acquire access token for authentication
      const token = await getAccessToken();

      // Create Socket.IO client
      this.socket = io(wsUrl, {
        path: '/api/ws',
        auth: {
          token
        },
        transports: ['websocket', 'polling'], // Prefer WebSocket, fallback to polling
        reconnection: true,
        reconnectionAttempts: this.maxReconnectAttempts,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 30000,
        timeout: 20000,
        autoConnect: options.autoConnect !== false,
        ...options
      });

      this.setupConnectionHandlers();
      this.setupMessageTracking();
      this.isInitialized = true;

      // Notify any hooks that were waiting for initialization
      for (const cb of this.initializationSubscribers) {
        try {
          cb();
        } catch (callbackError) {
          Logger.warn('RealtimeClient: initialization subscriber callback error', {
            error: callbackError.message
          });
        }
      }
      this.initializationSubscribers.clear();

      Logger.info('RealtimeClient initialized', {
        url: wsUrl,
        path: '/api/ws'
      });
    } catch (error) {
      Logger.error('Failed to initialize RealtimeClient', error);
      throw error;
    }
  }

  /**
   * Setup connection event handlers
   * @private
   */
  setupConnectionHandlers() {
    if (!this.socket) return;

    // Connection established
    this.socket.on(EVENTS.CONNECT, () => {
      this.isConnected = true;
      this.reconnectAttempts = 0;
      this.connectedAt = new Date();
      this.messageCount = 0; // Reset message count on new connection

      Logger.info('WebSocket connected', {
        socketId: this.socket.id
      });

      // Track connection success metric
      appInsightsService.trackEvent('WebSocket:Connected', {
        socketId: this.socket.id,
        transport: this.socket.io?.engine?.transport?.name || 'unknown'
      });

      // Track active connection gauge
      appInsightsService.trackMetric('WebSocket:ActiveConnection', 1, {
        socketId: this.socket.id
      });

      this.notifyConnectionStatus({ isConnected: true, error: null });
      connectionHealthService.recordSuccess();
    });

    // Connection lost
    this.socket.on(EVENTS.DISCONNECT, reason => {
      this.isConnected = false;

      // Calculate connection duration
      const duration = this.connectedAt ? Date.now() - this.connectedAt.getTime() : 0;

      Logger.warn('WebSocket disconnected', {
        reason,
        socketId: this.socket.id
      });

      // Track disconnection event
      appInsightsService.trackEvent('WebSocket:Disconnected', {
        socketId: this.socket.id,
        reason,
        durationMs: duration,
        messagesReceived: this.messageCount
      });

      // Track connection duration metric
      if (duration > 0) {
        appInsightsService.trackMetric('WebSocket:ConnectionDuration', duration, {
          reason,
          messagesReceived: this.messageCount
        });
      }

      // Track active connection gauge (now 0)
      appInsightsService.trackMetric('WebSocket:ActiveConnection', 0, {
        socketId: this.socket.id
      });

      this.notifyConnectionStatus({ isConnected: false, error: reason });
    });

    // Connection error
    this.socket.on(EVENTS.CONNECT_ERROR, error => {
      Logger.error('WebSocket connection error', error);

      // Track connection error
      appInsightsService.trackEvent('WebSocket:ConnectionError', {
        errorType: error.type || 'unknown',
        errorMessage: error.message,
        reconnectAttempt: this.reconnectAttempts
      });

      appInsightsService.trackException(error, {
        component: 'RealtimeClient',
        errorType: 'connection_error'
      });

      this.notifyConnectionStatus({
        isConnected: false,
        error: error.message
      });
      connectionHealthService.recordFailure(error);
    });

    // Reconnection attempt
    this.socket.on(EVENTS.RECONNECT_ATTEMPT, attemptNumber => {
      this.reconnectAttempts = attemptNumber;
      Logger.info('WebSocket reconnection attempt', {
        attempt: attemptNumber,
        maxAttempts: this.maxReconnectAttempts
      });

      // Track reconnection attempt
      appInsightsService.trackEvent('WebSocket:ReconnectionAttempt', {
        attemptNumber,
        maxAttempts: this.maxReconnectAttempts
      });

      this.notifyConnectionStatus({
        isConnected: false,
        isReconnecting: true,
        reconnectAttempt: attemptNumber
      });
    });

    // Reconnection successful
    this.socket.on(EVENTS.RECONNECT, attemptNumber => {
      Logger.info('WebSocket reconnected', {
        afterAttempts: attemptNumber
      });

      // Track successful reconnection
      appInsightsService.trackEvent('WebSocket:ReconnectionSuccess', {
        attemptNumber,
        socketId: this.socket.id
      });

      appInsightsService.trackMetric('WebSocket:ReconnectionAttempts', attemptNumber, {
        success: true
      });

      this.reconnectAttempts = 0;
      this.notifyConnectionStatus({
        isConnected: true,
        error: null,
        isReconnecting: false
      });
      connectionHealthService.recordSuccess();
    });

    // Reconnection failed
    this.socket.on(EVENTS.RECONNECT_FAILED, () => {
      Logger.error('WebSocket reconnection failed', {
        maxAttempts: this.maxReconnectAttempts
      });

      // Track reconnection failure
      appInsightsService.trackEvent('WebSocket:ReconnectionFailed', {
        maxAttempts: this.maxReconnectAttempts
      });

      appInsightsService.trackMetric('WebSocket:ReconnectionAttempts', this.maxReconnectAttempts, {
        success: false
      });

      this.notifyConnectionStatus({
        isConnected: false,
        error: 'Reconnection failed',
        isReconnecting: false
      });
    });

    // Token expiration events
    this.setupAuthEventHandlers();
  }

  /**
   * Setup authentication event handlers for token expiration
   * @private
   */
  setupAuthEventHandlers() {
    if (!this.socket) return;

    // Token expired - requires re-authentication
    this.socket.on(EVENTS.AUTH_EXPIRED, async data => {
      Logger.warn('WebSocket authentication expired', {
        reason: data.reason,
        message: data.message
      });

      // Track auth expiration
      appInsightsService.trackEvent('WebSocket:AuthExpired', {
        reason: data.reason
      });

      // Notify status subscribers
      this.notifyConnectionStatus({
        isConnected: false,
        error: 'Authentication expired',
        authExpired: true
      });

      // Attempt to refresh authentication
      try {
        await this.refreshAuth();
        Logger.info('WebSocket re-authenticated after expiration');

        appInsightsService.trackEvent('WebSocket:ReauthSuccess', {
          reason: 'token_expired'
        });
      } catch (error) {
        Logger.error('Failed to re-authenticate WebSocket', error);

        appInsightsService.trackEvent('WebSocket:ReauthFailed', {
          error: error.message
        });

        // Notify that re-auth failed
        this.notifyConnectionStatus({
          isConnected: false,
          error: 'Re-authentication failed',
          authExpired: true,
          requiresLogin: true
        });
      }
    });

    // Token expiring soon - warning to refresh
    this.socket.on(EVENTS.AUTH_EXPIRING, async data => {
      Logger.info('WebSocket authentication expiring soon', {
        secondsUntilExpiry: data.secondsUntilExpiry
      });

      // Track auth expiring warning
      appInsightsService.trackEvent('WebSocket:AuthExpiringSoon', {
        secondsUntilExpiry: data.secondsUntilExpiry
      });

      // Notify status subscribers
      this.notifyConnectionStatus({
        isConnected: this.isConnected,
        authExpiringSoon: true,
        secondsUntilExpiry: data.secondsUntilExpiry
      });

      // Proactively refresh authentication before expiry
      try {
        await this.refreshAuth();
        Logger.info('WebSocket proactively re-authenticated before expiry');

        appInsightsService.trackEvent('WebSocket:ProactiveReauth', {
          secondsUntilExpiry: data.secondsUntilExpiry
        });
      } catch (error) {
        Logger.warn('Failed to proactively refresh WebSocket auth', error);
        // Not critical - we still have time before actual expiry
      }
    });
  }

  /**
   * Setup message tracking for performance metrics
   * @private
   */
  setupMessageTracking() {
    if (!this.socket) return;

    // Track all business events
    const businessEvents = [
      EVENTS.BUS_STATUS_UPDATE,
      EVENTS.BUS_LOCATION_UPDATE,
      EVENTS.BUS_CREATED,
      EVENTS.BUS_UPDATED,
      EVENTS.BUS_DELETED,
      EVENTS.SCHEDULE_CREATED,
      EVENTS.SCHEDULE_UPDATED,
      EVENTS.SCHEDULE_DELETED,
      EVENTS.MAINTENANCE_CREATED,
      EVENTS.MAINTENANCE_UPDATED,
      EVENTS.MAINTENANCE_DELETED,
      EVENTS.MAINTENANCE_ALERT,
      EVENTS.DASHBOARD_REFRESH,
      EVENTS.METRICS_UPDATE,
      EVENTS.LOCATION_UPDATE,
      EVENTS.VEHICLE_LOCATION,
      EVENTS.GEOFENCE_ENTRY,
      EVENTS.GEOFENCE_EXIT,
      EVENTS.GEOFENCE_ALERT
    ];

    businessEvents.forEach(eventType => {
      this.socket.on(eventType, data => {
        this.messageCount++;
        const now = Date.now();
        this.lastMessageTime = now;

        // Calculate latency if timestamp is included in message
        const latency = data?.timestamp ? now - new Date(data.timestamp).getTime() : null;

        // Track message received
        appInsightsService.trackEvent('WebSocket:MessageReceived', {
          eventType,
          hasLatency: latency !== null
        });

        // Track throughput metric
        appInsightsService.trackMetric('WebSocket:MessageThroughput', 1, {
          eventType
        });

        // Track latency if available
        if (latency !== null && latency >= 0) {
          appInsightsService.trackMetric('WebSocket:MessageLatency', latency, {
            eventType
          });
        }
      });
    });
  }

  /**
   * Subscribe to a specific event
   *
   * IMPORTANT: Always call the returned unsubscribe function in cleanup!
   * For React components, use the useRealtimeEvent hook instead which handles
   * cleanup automatically.
   *
   * @param {string} event - Event name
   * @param {Function} handler - Event handler function
   * @returns {Function} Unsubscribe function - MUST be called in cleanup!
   * @example
   * // In a React useEffect:
   * useEffect(() => {
   *   const unsubscribe = realtimeClient.on('event', handler);
   *   return () => unsubscribe(); // Required cleanup!
   * }, []);
   *
   * // Better: use the useRealtimeEvent hook which handles cleanup automatically
   * import { useRealtimeEvent, EVENTS } from '../hooks/useRealtimeEvent';
   * useRealtimeEvent(EVENTS.BUS_STATUS_UPDATE, handler);
   */
  on(event, handler) {
    if (!this.socket) {
      Logger.warn('Socket not initialized, cannot subscribe to event', { event });
      return () => {};
    }

    // Store handler for cleanup
    if (!this.eventHandlers.has(event)) {
      this.eventHandlers.set(event, new Set());
    }
    const handlers = this.eventHandlers.get(event);
    handlers.add(handler);

    // Check for potential memory leak - warn if handler count exceeds threshold
    const HANDLER_WARN_THRESHOLD = 10;
    const HANDLER_ERROR_THRESHOLD = 20;

    if (handlers.size > HANDLER_ERROR_THRESHOLD) {
      Logger.error('Possible memory leak: Excessive handlers for event', {
        event,
        handlerCount: handlers.size,
        threshold: HANDLER_ERROR_THRESHOLD
      });

      // Track memory leak metric
      appInsightsService.trackEvent('WebSocket:PossibleMemoryLeak', {
        event,
        handlerCount: handlers.size,
        severity: 'error'
      });
    } else if (handlers.size > HANDLER_WARN_THRESHOLD) {
      Logger.warn('Many handlers for event - check for missing cleanup', {
        event,
        handlerCount: handlers.size,
        threshold: HANDLER_WARN_THRESHOLD
      });

      // Track warning metric
      appInsightsService.trackEvent('WebSocket:HandlerAccumulation', {
        event,
        handlerCount: handlers.size,
        severity: 'warning'
      });
    }

    // Track total handler count periodically
    const totalHandlers = this.getTotalHandlerCount();
    if (totalHandlers % 10 === 0) {
      // Track every 10th handler registration
      appInsightsService.trackMetric('WebSocket:TotalHandlerCount', totalHandlers, {
        eventTypes: this.eventHandlers.size
      });
    }

    // Register with Socket.IO
    this.socket.on(event, handler);

    Logger.debug('Subscribed to event', {
      event,
      handlerCount: handlers.size
    });

    // Return unsubscribe function
    return () => this.off(event, handler);
  }

  /**
   * Get the total count of registered event handlers across all events
   * @returns {number} Total handler count
   */
  getTotalHandlerCount() {
    let total = 0;
    this.eventHandlers.forEach(handlers => {
      total += handlers.size;
    });
    return total;
  }

  /**
   * Get handler count statistics for debugging and monitoring
   * @returns {Object} Handler statistics
   */
  getHandlerStats() {
    const stats = {
      totalHandlers: 0,
      eventTypes: this.eventHandlers.size,
      byEvent: {}
    };

    this.eventHandlers.forEach((handlers, event) => {
      stats.totalHandlers += handlers.size;
      stats.byEvent[event] = handlers.size;
    });

    return stats;
  }

  /**
   * Unsubscribe from a specific event
   * @param {string} event - Event name
   * @param {Function} handler - Event handler function
   */
  off(event, handler) {
    if (!this.socket) return;

    this.socket.off(event, handler);

    const handlers = this.eventHandlers.get(event);
    if (handlers) {
      handlers.delete(handler);
      if (handlers.size === 0) {
        this.eventHandlers.delete(event);
      }
    }

    Logger.debug('Unsubscribed from event', { event });
  }

  /**
   * Emit an event to the server
   * @param {string} event - Event name
   * @param {*} data - Event data
   */
  emit(event, data) {
    if (!this.socket || !this.isConnected) {
      Logger.warn('Cannot emit event, socket not connected', { event });

      // Track emit failure
      appInsightsService.trackEvent('WebSocket:EmitFailed', {
        event,
        reason: this.socket ? 'not_connected' : 'not_initialized'
      });
      return;
    }

    this.socket.emit(event, data);
    Logger.debug('Emitted event', { event, data });

    // Track emit success
    appInsightsService.trackEvent('WebSocket:EmitSuccess', {
      event
    });
  }

  /**
   * Subscribe to connection status changes
   * @param {Function} callback - Status change callback
   * @returns {Function} Unsubscribe function
   */
  onConnectionStatusChange(callback) {
    this.connectionStatusSubscribers.add(callback);

    // Immediately notify with current status
    callback({
      isConnected: this.isConnected,
      reconnectAttempt: this.reconnectAttempts
    });

    // Return unsubscribe function
    return () => {
      this.connectionStatusSubscribers.delete(callback);
    };
  }

  /**
   * Subscribe to client initialization.
   * If already initialized, the callback is invoked synchronously.
   * @param {Function} callback - Called once when initialization completes
   * @returns {Function} Unsubscribe function
   */
  onInitialized(callback) {
    if (this.isInitialized) {
      callback();
      return () => {};
    }
    this.initializationSubscribers.add(callback);
    return () => this.initializationSubscribers.delete(callback);
  }

  /**
   * Notify all connection status subscribers
   * @private
   * @param {Object} status - Connection status
   */
  notifyConnectionStatus(status) {
    this.connectionStatusSubscribers.forEach(callback => {
      try {
        callback(status);
      } catch (error) {
        Logger.error('Error in connection status callback', error);
      }
    });
  }

  /**
   * Manually connect the socket
   */
  connect() {
    if (!this.socket) {
      Logger.error('Socket not initialized');
      return;
    }

    if (!this.isConnected) {
      this.socket.connect();
      Logger.info('Manually connecting WebSocket');
    }
  }

  /**
   * Manually disconnect the socket
   */
  disconnect() {
    if (!this.socket) return;

    this.socket.disconnect();
    Logger.info('WebSocket disconnected manually');
  }

  /**
   * Refresh authentication token
   * Disconnects and reconnects with new token
   */
  async refreshAuth() {
    try {
      const token = await getAccessToken();

      if (this.socket) {
        // Update auth token
        this.socket.auth = { token };

        // Reconnect if currently connected
        if (this.isConnected) {
          this.disconnect();
          this.connect();
          Logger.info('WebSocket authentication refreshed');
        }
      }
    } catch (error) {
      Logger.error('Failed to refresh WebSocket authentication', error);
      throw error;
    }
  }

  /**
   * Get connection state
   * @returns {Object} Connection state
   */
  getConnectionState() {
    return {
      isConnected: this.isConnected,
      isInitialized: this.isInitialized,
      socketId: this.socket?.id,
      reconnectAttempts: this.reconnectAttempts
    };
  }

  /**
   * Cleanup and disconnect
   */
  destroy() {
    if (this.socket) {
      // Remove all event listeners
      this.eventHandlers.forEach((handlers, event) => {
        handlers.forEach(handler => {
          this.socket.off(event, handler);
        });
      });
      this.eventHandlers.clear();

      // Disconnect
      this.socket.disconnect();
      this.socket = null;
    }

    this.connectionStatusSubscribers.clear();
    this.initializationSubscribers.clear();
    this.isConnected = false;
    this.isInitialized = false;

    Logger.info('RealtimeClient destroyed');
  }
}

// Export singleton instance
export const realtimeClient = new RealtimeClient();

// Export events for convenience
export { EVENTS };

// Export initialization function
export async function initializeRealtimeConnection(options) {
  await realtimeClient.initialize(options);
  return realtimeClient;
}
